import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight, ExternalLink, ShieldCheck } from 'lucide-react';

import {
  useUserOrganizationsQuery,
  type UserOrganizationRow,
} from '@/client/api/admin/user-content.query';
import {
  useAdminOrganizationDetailQuery,
  useAdminOrganizationListingsQuery,
} from '@/client/api/admin/organizations';
import OverviewTab from '@/client/pages/Admin/Organizations/OrganizationDetailPage/tabs/OverviewTab';
import MembersTab from '@/client/pages/Admin/Organizations/OrganizationDetailPage/tabs/MembersTab';
import InvitesTab from '@/client/pages/Admin/Organizations/OrganizationDetailPage/tabs/InvitesTab';
import { Skeleton } from '@/components/ui/skeleton';

import EntityTable from './components/EntityTable';

type SubTab = 'overview' | 'members' | 'invites' | 'listings';

const SUBTABS: { key: SubTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'members', label: 'Members' },
  { key: 'invites', label: 'Invites' },
  { key: 'listings', label: 'Listings' },
];

/* ───────────────────────── Org listings ────────────────────────────────── */

const OrgListingsSection = ({ oid }: { oid: number }) => {
  const { data, isLoading, isError } = useAdminOrganizationListingsQuery(oid, {
    page: 1,
    page_size: 12,
  });
  const entities = data?.entities ?? [];

  return (
    <section className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-800">
        Listings
        {data?.total_entities != null && (
          <span className="ml-2 text-xs font-normal text-gray-400">
            {data.total_entities} total
          </span>
        )}
      </h4>
      <EntityTable
        entities={entities}
        isLoading={isLoading}
        isError={isError}
        emptyMessage="This organization has no listings."
      />
    </section>
  );
};

/* ───────────────────────── Selected-org management ─────────────────────── */

const OrgManagementPanel = ({ org: row }: { org: UserOrganizationRow }) => {
  const oid = row.oid;
  const [sub, setSub] = useState<SubTab>('overview');

  const detailQuery = useAdminOrganizationDetailQuery(oid);
  const fullOrg = detailQuery.data;

  const verified = (fullOrg?.verified ?? row.verified) === 1;
  const onHold = (fullOrg?.admin_hold ?? row.admin_hold) === 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 pb-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{row.name}</h3>
            {verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                <ShieldCheck className="h-3 w-3" /> Verified
              </span>
            )}
            {onHold && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                On hold
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-400">
            oid {oid}
            {row.membership ? ` · ${row.membership}` : ''}
          </p>
        </div>
        <Link
          to={`/admin/organizations/${oid}`}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          Open full page
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-6" role="tablist">
          {SUBTABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={sub === key}
              onClick={() => setSub(key)}
              className={`-mb-px border-b-2 px-1 pb-2.5 text-sm font-medium transition-colors ${
                sub === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {sub === 'overview' &&
        (detailQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : detailQuery.isFetchError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-900">
            Couldn&apos;t load this organization&apos;s profile. Members, invites and listings
            below still work — use{' '}
            <Link to={`/admin/organizations/${oid}`} className="font-medium underline">
              the full org page
            </Link>{' '}
            to retry.
          </div>
        ) : fullOrg ? (
          <OverviewTab oid={oid} org={fullOrg} />
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
            Organization not found. Use{' '}
            <Link to={`/admin/organizations/${oid}`} className="font-medium underline">
              the full org page
            </Link>{' '}
            for more detail.
          </div>
        ))}
      {sub === 'members' && <MembersTab oid={oid} />}
      {sub === 'invites' && <InvitesTab oid={oid} />}
      {sub === 'listings' && <OrgListingsSection oid={oid} />}
    </div>
  );
};

/* ───────────────────────── Tab root ────────────────────────────────────── */

const OrganizationsTab = ({ uid }: { uid: number }) => {
  const { data, isLoading, isError } = useUserOrganizationsQuery(uid);
  const orgs: UserOrganizationRow[] = data?.organizations ?? [];
  const [selectedOid, setSelectedOid] = useState<number | null>(null);

  const selected = orgs.find(o => o.oid === selectedOid) ?? null;

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
        <Building2 className="mx-auto mb-2 h-6 w-6 text-amber-500" />
        Couldn&apos;t load this user&apos;s organizations. This tab reads{' '}
        <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">
          GET /api/admin/user/info/{uid}/organizations
        </code>
        .
      </div>
    );
  }

  if (orgs.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
        This user does not own or belong to any organizations.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <aside className="w-full flex-shrink-0 lg:w-72">
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {orgs.map(org => {
            const isSel = org.oid === selectedOid;
            return (
              <li key={org.oid}>
                <button
                  onClick={() => setSelectedOid(org.oid)}
                  className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors ${
                    isSel ? 'bg-indigo-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate text-sm font-medium ${
                        isSel ? 'text-indigo-700' : 'text-gray-900'
                      }`}
                    >
                      {org.name}
                    </p>
                    <p className="truncate text-xs text-gray-400">
                      oid {org.oid}
                      {org.membership ? ` · ${org.membership}` : ''}
                    </p>
                  </div>
                  <ChevronRight
                    className={`h-4 w-4 flex-shrink-0 ${
                      isSel ? 'text-indigo-500' : 'text-gray-300'
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <div className="min-w-0 flex-1">
        {selected ? (
          <OrgManagementPanel key={selected.oid} org={selected} />
        ) : (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
            Select an organization to manage its members, invites and listings.
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationsTab;
